/* Dateien im geschützten Speicher.
 *
 * Der Bucket ist privat. Jeder Zugriff läuft über einen zeitlich begrenzten
 * signierten Link — es gibt keine öffentlichen Adressen.
 *
 * Die Pfade kodieren den Bereich (avatars/, identity/, clubContracts/ …).
 * Darauf setzen die geplanten Zugriffsregeln auf, siehe docs/rollen-konzept.md.
 */

import { supabase, STORAGE_BUCKET } from './client.js';
import { derivedCandidates, isDerivedPath, pickSigned } from '../domain/photoVariants.js';

const SIGNED_SECONDS = 3600;
/* Etwas früher verwerfen als der Link abläuft, damit kein Bild mitten im
   Betrachten ungültig wird. */
const CACHE_MS = (SIGNED_SECONDS - 300) * 1000;

const cache = new Map();

export async function signedUrl(path) {
  if (!supabase || !path) return null;

  const hit = cache.get(path);
  if (hit && hit.expires > Date.now()) return hit.url;

  const { data, error } = await supabase.storage
    .from(STORAGE_BUCKET)
    .createSignedUrl(path, SIGNED_SECONDS);

  if (error) {
    console.error('[storage] Signierter Link fehlgeschlagen:', path, error.message);
    return null;
  }

  cache.set(path, { url: data.signedUrl, expires: Date.now() + CACHE_MS });
  return data.signedUrl;
}

/* Abgeleitete Fassungen, die es (noch) nicht gibt — alte Fotos. Damit wird je
   Sitzung nur einmal danach gefragt. Schlüssel: "<Fassung>|<Originalpfad>".
   Laufende Abfragen werden zusammengelegt. */
const missing = new Set();
const inflight = new Map();

/**
 * Adresse eines Bildes in der gewünschten Fassung ('thumb', 'md' oder
 * 'original'). Eine Fassung kann als webp, png (neu, mit Transparenz) oder jpg
 * (Altbestand, weißer Hintergrund) vorliegen; alle Kandidaten und das Original
 * werden in EINER Anfrage signiert, die bevorzugte vorhandene Fassung gewinnt
 * (domain/photoVariants.js pickSigned). Gibt es keine, kommt das Original — ohne
 * Fehler und ohne zusätzliche Abfrage.
 */
export async function signedImageUrl(path, variant = 'original') {
  if (!supabase || !path) return null;
  const candidates = derivedCandidates(path, variant);
  if (candidates.length === 1 && candidates[0] === path) return signedUrl(path);

  for (const c of candidates) {
    const hit = cache.get(c);
    if (hit && hit.expires > Date.now()) return hit.url;
  }
  const key = `${variant}|${path}`;
  if (missing.has(key)) return signedUrl(path);

  if (inflight.has(key)) return inflight.get(key);
  const job = (async () => {
    const { data, error } = await supabase.storage
      .from(STORAGE_BUCKET)
      .createSignedUrls([...candidates, path], SIGNED_SECONDS);
    if (error || !data) {
      console.error('[storage] Signierte Links fehlgeschlagen:', path, error && error.message);
      return signedUrl(path);
    }
    const picked = pickSigned(data);
    const expires = Date.now() + CACHE_MS;
    if (picked.used === 'variant') {
      cache.set(candidates[picked.index], { url: picked.url, expires });
    } else {
      missing.add(key);
      if (picked.used === 'original') cache.set(path, { url: picked.url, expires });
    }
    return picked.url;
  })();
  inflight.set(key, job);
  try {
    return await job;
  } finally {
    inflight.delete(key);
  }
}

/** Welche dieser Pfade gibt es im Speicher? Liefert eine Liste mit true/false in derselben Reihenfolge. */
export async function existsMany(paths) {
  if (!supabase) throw new Error('notConnected');
  if (!paths.length) return [];
  const { data, error } = await supabase.storage
    .from(STORAGE_BUCKET)
    .createSignedUrls(paths, 60);
  if (error || !data) {
    console.error('[storage] Prüfung fehlgeschlagen:', error && error.message);
    throw new Error('loadFailed');
  }
  return data.map((e) => !!(e && e.signedUrl && !e.error));
}

/**
 * Datei KOPIEREN (nie verschieben): Die Quelle bleibt liegen. Für das Übernehmen der Vereinslogos ins
 * Register (domain/clubLogoCopy.js). Gibt es das Ziel schon (Wiederaufnahme nach einem Abbruch), gilt das
 * als Erfolg, sobald die Datei dort wirklich vorhanden ist; ein vorhandenes Ziel wird nie überschrieben.
 * Wirft 'copyFailed'.
 */
export async function copyFile(from, to) {
  if (!supabase) throw new Error('notConnected');
  const { error } = await supabase.storage.from(STORAGE_BUCKET).copy(from, to);
  if (!error) return to;
  const alreadyThere = /already exists|duplicate|409/i.test(String(error.message || '') + String(error.statusCode || ''));
  if (alreadyThere) {
    const [present] = await existsMany([to]).catch(() => [false]);
    if (present) return to;
  }
  console.error('[storage] Kopieren fehlgeschlagen:', from, '->', to, error.message);
  throw new Error('copyFailed');
}

/** Datei als Blob laden (für das Verkleinern im Browser). */
export async function downloadBlob(path) {
  const href = await signedUrl(path);
  if (!href) throw new Error('loadFailed');
  const res = await fetch(href);
  if (!res.ok) throw new Error('loadFailed');
  return res.blob();
}

/**
 * Datei unter genau diesem Pfad ablegen (ohne Zufallsanteil). Standard: ohne Überschreiben.
 * `overwrite` ist NUR für abgeleitete Fassungen (.thumb/.md) erlaubt, damit "Alle neu
 * erzeugen" eine vorhandene Fassung ersetzen kann; ein Original wird nie überschrieben
 * (der Aufruf wirft, bevor etwas gesendet wird).
 * contentType: der tatsächliche Typ der Datei (z. B. image/webp), sonst der des Blobs.
 */
export async function uploadFileAt(path, blob, { overwrite = false, contentType } = {}) {
  if (!supabase) throw new Error('notConnected');
  if (overwrite && !isDerivedPath(path)) throw new Error('originalProtected');
  const { error } = await supabase.storage
    .from(STORAGE_BUCKET)
    .upload(path, blob, { upsert: overwrite, contentType: contentType || blob.type || undefined });
  if (error) {
    console.error('[storage] Hochladen fehlgeschlagen:', error.message);
    throw new Error('uploadFailed');
  }
  /* Neue oder ersetzte Datei: gemerkte "fehlt"-Antworten und alte Links verwerfen */
  missing.clear();
  cache.delete(path);
  return path;
}

/** Cache leeren — nötig nach einem Nutzerwechsel, da Links nutzergebunden sind. */
export function clearUrlCache() {
  cache.clear();
  missing.clear();
}

export async function uploadFile(file, folder) {
  if (!supabase) throw new Error('notConnected');

  const ext = (file.name.split('.').pop() || 'bin').toLowerCase();
  const path = `${folder}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;

  const { error } = await supabase.storage
    .from(STORAGE_BUCKET)
    .upload(path, file, { upsert: false });

  if (error) {
    console.error('[storage] Hochladen fehlgeschlagen:', error.message);
    throw new Error('uploadFailed');
  }
  return path;
}

/**
 * Mehrere Dateien löschen. Liefert { failed }: Pfade, die nicht gelöscht werden konnten. Eine
 * Speicherregel, die das Löschen verweigert, meldet keinen Fehler, sie lässt die Datei still
 * liegen — deshalb zählt, was die Antwort als gelöscht nennt.
 */
export async function removeFiles(paths) {
  if (!supabase) throw new Error('notConnected');
  const failed = [];
  for (let i = 0; i < paths.length; i += 100) {
    const chunk = paths.slice(i, i + 100);
    const { data, error } = await supabase.storage.from(STORAGE_BUCKET).remove(chunk);
    if (error) {
      console.error('[storage] Löschen fehlgeschlagen:', error.message);
      failed.push(...chunk);
      continue;
    }
    const gone = new Set((data || []).map((o) => o.name));
    /* Pfade, die es nie gab (z. B. nicht erzeugte Fassungen), tauchen nicht in der Antwort auf und
       sind kein Fehler: Es gilt nur, was die Datei noch gibt. */
    const stillThere = chunk.filter((p) => !gone.has(p));
    if (stillThere.length) {
      const present = await existsMany(stillThere).catch(() => stillThere.map(() => true));
      stillThere.forEach((p, k) => { if (present[k]) failed.push(p); });
    }
  }
  paths.forEach((p) => cache.delete(p));
  return { failed };
}

async function listNames(folder, options = {}) {
  const { data, error } = await supabase.storage
    .from(STORAGE_BUCKET)
    .list(folder, { limit: 1000, ...options });
  if (error) {
    console.error('[storage] Auflisten fehlgeschlagen:', error.message);
    throw new Error('loadFailed');
  }
  return data || [];
}

/**
 * Alle Dateien eines Spielers im Speicher. Jede Datei liegt unter <Ordner>/<Spieler-ID>/ (Fotos
 * unter avatars/<ID>/, Vereinslogos unter avatars/club-<ID>/). Gesucht wird in allen obersten Ordnern
 * des Buckets, damit auch Ordner gefunden werden, die diese Liste nicht kennt. Wirft bei Fehler.
 * Ohne Löschrecht reicht die Leseregel (admin, board, data sehen alles; die App ruft das nur für admin).
 */
export async function listPlayerFiles(playerId, excludeFolders = []) {
  if (!supabase) throw new Error('notConnected');
  const top = (await listNames('')).map((e) => e.name).filter((n) => n && !excludeFolders.includes(n));
  const out = [];
  for (const folder of top) {
    for (const f of await listNames(`${folder}/${playerId}`)) {
      if (f.id) out.push(`${folder}/${playerId}/${f.name}`);
    }
  }
  // Vereinslogos: avatars/club-<ID>/
  for (const f of await listNames(`avatars/club-${playerId}`)) {
    if (f.id) out.push(`avatars/club-${playerId}/${f.name}`);
  }
  return out;
}

export async function removeFile(path) {
  if (!supabase || !path) return;
  const { error } = await supabase.storage.from(STORAGE_BUCKET).remove([path]);
  if (error) console.error('[storage] Löschen fehlgeschlagen:', error.message);
  cache.delete(path);
}
